import React, { useState, useRef, useEffect } from "react";

// Desktop lists sidebar (design source). My Radar is always first and carries no
// ⋯ menu (not renamable, not deletable); custom lists get Rename / Delete list.
// Inline rename commits on Enter/blur; "+ New list" opens an above-anchored
// popover. Parent owns the data + the delete confirm; this owns the local popover
// state and closes menus on outside click.
const ListsSidebar = ({ lists, selectedList, onSelect, onCreateList, onRenameList, onDeleteRequest }) => {
  const [menuId, setMenuId] = useState(null);
  const [renameId, setRenameId] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const rootRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setMenuId(null);
        setNewOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const startRename = (l) => {
    setMenuId(null);
    setRenameId(l.id);
    setRenameVal(l.name);
  };

  const commitRename = () => {
    if (!renameId) return;
    const v = renameVal.trim();
    if (v) onRenameList(renameId, v);
    setRenameId(null);
    setRenameVal("");
  };

  const commitCreate = () => {
    const v = newName.trim();
    if (v) onCreateList(v);
    setNewOpen(false);
    setNewName("");
  };

  return (
    <div
      ref={rootRef}
      style={{ background: "#FFFFFF", border: "1px solid #E4E9F0", borderRadius: 14, boxShadow: "0 1px 3px rgba(0,0,0,0.06)", padding: 8 }}
    >
      <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-[#9CA3AF]" style={{ padding: "8px 10px 6px" }}>
        Lists
      </div>

      {lists.map((l) => {
        const selected = selectedList === l.id;
        const isCustom = !l.is_default;
        if (renameId === l.id) {
          return (
            <div key={l.id} style={{ padding: "4px 6px" }}>
              <input
                value={renameVal}
                onChange={(e) => setRenameVal(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitRename();
                  if (e.key === "Escape") { setRenameId(null); setRenameVal(""); }
                }}
                onBlur={commitRename}
                autoFocus
                className="w-full outline-none"
                style={{ height: 30, border: "1px solid #2283FF", borderRadius: 9, padding: "0 9px", fontSize: 13 }}
              />
            </div>
          );
        }
        return (
          <div key={l.id} className="relative">
            <div
              onClick={() => onSelect(l.id)}
              className="flex items-center cursor-pointer"
              style={{
                gap: 8,
                padding: "8px 10px",
                borderRadius: 9,
                fontSize: 13.5,
                background: selected ? "#EAF2FF" : "transparent",
                color: selected ? "#1B6FE0" : "#111827",
              }}
            >
              <div className="flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap font-semibold">{l.name}</div>
              <div className="text-[11.5px] text-[#9CA3AF] font-semibold">{l.entry_count ?? 0}</div>
              {isCustom && (
                <button
                  type="button"
                  aria-label={`${l.name} options`}
                  onClick={(e) => { e.stopPropagation(); setMenuId(menuId === l.id ? null : l.id); }}
                  className="border-none bg-transparent text-[#9CA3AF] hover:text-[#374151] cursor-pointer leading-none"
                  style={{ fontSize: 14, fontWeight: 700, padding: "0 2px" }}
                >
                  ⋯
                </button>
              )}
            </div>
            {menuId === l.id && (
              <div
                className="absolute z-30"
                style={{ right: 6, top: 36, minWidth: 150, background: "#FFFFFF", border: "1px solid #E4E9F0", borderRadius: 9, boxShadow: "0 8px 24px rgba(17,24,39,0.12)", padding: 5 }}
              >
                <button
                  type="button"
                  onClick={() => startRename(l)}
                  className="block w-full text-left bg-transparent border-none rounded-md text-[12.5px] text-[#374151] font-medium cursor-pointer hover:bg-[#F3F6FA]"
                  style={{ padding: "7px 10px" }}
                >
                  Rename
                </button>
                <button
                  type="button"
                  onClick={() => { setMenuId(null); onDeleteRequest(l); }}
                  className="block w-full text-left bg-transparent border-none rounded-md text-[12.5px] text-[#DC2626] font-medium cursor-pointer hover:bg-[#FEF2F2]"
                  style={{ padding: "7px 10px" }}
                >
                  Delete list
                </button>
              </div>
            )}
          </div>
        );
      })}

      <div className="relative" style={{ marginTop: 6, padding: "4px 6px 6px" }}>
        <button
          type="button"
          onClick={() => { setNewOpen((v) => !v); setNewName(""); }}
          className="w-full bg-white hover:bg-[#F8FAFC] text-[#374151] font-semibold cursor-pointer"
          style={{ border: "1px solid #DDE3EB", borderRadius: 9, padding: "7px 10px", fontSize: 12.5 }}
        >
          + New list
        </button>
        {newOpen && (
          <div
            className="absolute z-30"
            style={{ left: 6, right: 6, bottom: 44, background: "#FFFFFF", border: "1px solid #E4E9F0", borderRadius: 9, boxShadow: "0 12px 32px rgba(17,24,39,0.14)", padding: 10 }}
          >
            <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-[#9CA3AF]" style={{ marginBottom: 6 }}>New list</div>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitCreate();
                if (e.key === "Escape") setNewOpen(false);
              }}
              autoFocus
              placeholder="List name"
              maxLength={60}
              className="w-full outline-none"
              style={{ height: 32, border: "1px solid #DDE3EB", borderRadius: 9, padding: "0 9px", fontSize: 13, marginBottom: 8 }}
            />
            <div className="flex" style={{ gap: 6 }}>
              <button
                type="button"
                onClick={commitCreate}
                className="flex-1 bg-[#2283FF] hover:bg-[#1B6FE0] text-white border-none font-semibold cursor-pointer"
                style={{ borderRadius: 9, padding: "7px 0", fontSize: 12.5 }}
              >
                Create
              </button>
              <button
                type="button"
                onClick={() => { setNewOpen(false); setNewName(""); }}
                className="flex-1 bg-white hover:bg-[#F8FAFC] text-[#374151] font-semibold cursor-pointer"
                style={{ border: "1px solid #DDE3EB", borderRadius: 9, padding: "7px 0", fontSize: 12.5 }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ListsSidebar;
